import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_BLOCKLIST } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viProhibited } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import blocklistReducer from "./blocklistReducer";
import RuleEditor from "./ruleEditor/RuleEditor";

function Blocklist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);

  // Get general data
  useEffect(() => {
    Promise.all([Api.getPbx(), Api.getInbounds()]);
  }, []);

  const {
    value: blocklist,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_BLOCKLIST,
    reducer: blocklistReducer,
    validator: () => ({
      type: string().required({ message: "Type is required." }),
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." }),
    }),
    onSubmit: () => Api.storeBlocklist(blocklist),
    onSuccess: (response) => {
      if (!blocklist.id) {
        dispatch({ type: "id", value: response.data.blocklist.id });
        navigate(`../${response.data.blocklist.id}`);
      }

      Promise.resolve(Api.getBlocklists());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === blocklist.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_BLOCKLIST });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getBlocklist({ id: Number(id) }))
      .then((response) => !ignore && dispatch({ type: "set", value: response.data.blocklist }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [blocklist.id, dispatch, id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getBlocklists()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${blocklist.name || "Blocklist"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} /> <Avatar text={blocklist.name} icon={viProhibited} />
              </IconButton>
            ) : (
              <Avatar text={blocklist.name} icon={viProhibited} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={blocklist.id || "nk"}
                isActionable={!!blocklist?.id}
                onDeleteCheck={() => Api.deleteBlocklist(blocklist, false)}
                onDelete={() => Api.deleteBlocklist(blocklist, true)}
                name={blocklist.name}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={blocklist.name || "Unnamed blocklist"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputSelect
              disabled={!!blocklist.id}
              required={!blocklist.id}
              id="type"
              label="Type"
              onChange={formHandler}
              value={blocklist.type}
              selectValue="value"
              hasEmpty={null}
              selects={[
                { value: "user", title: "User" },
                { value: "pbx", title: "PBX" },
              ]}
              getOptionLabel={(option) => option.title}
              error={errors}
              helperText="Whether the blocklist is for the PBX or a user. You won't be able to change this afterwards."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={blocklist.name} error={errors} />
          </Grid>
          {blocklist.type === "pbx" && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputToggle
                id="allowExternalIn"
                onChange={formHandler}
                states={[false, true]}
                value={blocklist.allowExternalIn}
                primary="Allow external incoming calls"
                secondary={
                  !blocklist.allowExternalIn &&
                  blocklist.rules.filter(
                    (rule) =>
                      (rule.direction === "in" || (rule.direction === "both" && !blocklist.allowExternalOut)) &&
                      !rule.allowed,
                  ).length >= 1
                    ? "You have rules that are redundant when this is disabled."
                    : undefined
                }
                error={errors}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="allowExternalOut"
              onChange={formHandler}
              states={[false, true]}
              value={blocklist.allowExternalOut}
              primary="Allow external outgoing calls"
              secondary={
                !blocklist.allowExternalOut &&
                blocklist.rules.filter(
                  (rule) =>
                    (rule.direction === "out" || (rule.direction === "both" && !blocklist.allowExternalIn)) &&
                    !rule.allowed,
                ).length >= 1
                  ? "You have rules that are redundant when this is disabled."
                  : undefined
              }
              error={errors}
            />
          </Grid>
        </FormGroup>
        {blocklist.id && <RuleEditor key={blocklist.id} blocklist={blocklist} blocklistDispatch={dispatch} />}
      </Stack>
    </Container>
  );
}

export default Blocklist;
